import type {
  Branch,
  CompletionItem,
  EditRequest,
  Fixture,
  IdentifiedFinding,
  NamesTable,
  Plan,
  Projection,
  RowCounts,
  SidecarMap3,
  StructuralItem,
  StructureReport,
  SuppliedNames,
} from '../../../../dorian-shared/types/episodeScript';
import type { Branch as PlatformBranch } from '../../../../dorian-shared/types/branch/Branch';
import type { Story } from '../../../../dorian-shared/types/story/Story';
import type { User } from '../../../../dorian-shared/types/user/User';
import type { MemoryDTO } from '../../Book/MemoryBank/memoryBankTypes';
import type { ChangeWords } from '../viewModel/changeRows';

/** The debounce the compile half of the pipeline waits behind (spec §4.5). */
export const BASIL_DEBOUNCE_MS = 400;

/** How long a toast stays on the surface (editor.js:828-833). */
export const BASIL_TOAST_MS = 2400;

/**
 * The props the Story Branches page hands Basil (spec §4.2 and §9.2).
 *
 * The contract is the neighbour's — SelectiveEditModal's — with one addition, `limits`, because the
 * server does not bound step or answer text and `Steps.text` is a `VARCHAR(255)`: the client either
 * enforces the ceiling or loses the writer's words silently.
 */
export type BasilModalProps = {
  /** Whether the modal is open. The parent unmounts it when false. */
  show: boolean;
  /** `story?.id` from the page. */
  episodeId: number;
  /** `story?.book?.id` from the page; characters and locations are book-scoped. */
  bookId: number;
  /** `this.state.branch` — the page's node list. It has no `steps[]`, so it is a paint hint only. */
  preloadedNodes?: PlatformBranch[];
  /** `memoryBankContext.memories`; supplies the writer's word for a variable (spec §10.1 K8). */
  memoryBank?: MemoryDTO[];
  /** `settings.limits` from `GET /v1/settings`; 255 is the hard ceiling whatever it says. */
  limits?: Record<string, { value?: string }>;
  /** `isLiveEditDisabled(authUser.role, story.group)` — read-only when true. */
  isReadOnly?: boolean;
  /**
   * The page's `story`. The mount does not pass it today, and the write gate is fail-closed without
   * it: `canBasilWrite` cannot see the story's id or its group, so every apply refuses. Two lines in
   * `StoryBranches/index.js` (`story={story}` and `authUser={authUser}`) turn the write path on.
   */
  story?: Pick<Story, 'id' | 'group'> & Partial<Pick<Story, 'title' | 'chapter'>> | null;
  /** The page's `authUser`, for the same reason and with the same consequence when it is absent. */
  authUser?: Pick<User, 'id'> | null;
  /**
   * Accepted for parity with the neighbour's mount and ignored today: the page writes
   * `experimentalEpisodeDataFetch` nowhere, so the neighbour's flag is permanently false.
   */
  endpointVersion?: 'v1' | 'v2' | 'v3';
  /** Accepted for parity with the neighbour's mount: opens with the caret on that scene's heading. */
  initialBranchId?: number | null;
  /** Close and scroll the canvas to a node. */
  onNavigateToNode?: (nodeId: number) => void;
  /** Close and re-read: the parent calls `loadBranchList()`. */
  onSave?: () => void;
  /**
   * P4: hand the page a sentence to keep after Basil closes, until the writer dismisses it.
   *
   * "1 change applied and checked" is the answer to the question the writer asked when they pressed
   * Apply changes, and the success path CLOSES the editor — so the answer would otherwise vanish
   * with the surface that produced it. The page's toast auto-dismisses after four seconds, which is
   * not durable, so the mount renders a notice of its own.
   */
  onNotice?: (message: string) => void;
  /** Close without saving. */
  onHide: () => void;
};

/**
 * Why an apply is refused, in the writer's words. The two the spec fixes verbatim are the verifier
 * and the write log (spec section 9.4); the rest say what the write lane's own refusal code means
 * without ever printing the code at a writer.
 */
export const BASIL_REFUSAL_COPY: Readonly<Record<string, string>> = Object.freeze({
  'verifier-unavailable': "Basil's double-check isn't available right now, so changes are paused. You can still read and edit.",
  'audit-unavailable': 'Changes are paused because the change record needs attention. Check the Changes list for details.',
  'not-owner': "You don't have permission to change this episode. Ask its owner if you'd like to edit it.",
  'story-not-allowlisted': 'Basil can only read this episode, so nothing here will change.',
  'story-not-draft': 'This episode is already live, so Basil will only read it.',
  'live-denylist': 'This episode is already live, so Basil will only read it.',
  'plan-refused': 'Basil paused these changes. The Changes list shows why.',
  'needs-canary': "Basil can't make this kind of change yet.",
  'needs-owner': "This change needs the owner's approval first.",
  'stale-baseline': "Basil couldn't check this episode against the version you opened, so changes are paused. Reload it before applying.",
  'read-only': 'This episode is read only.',
  'nothing-to-apply': 'no changes',
  'subject-changed': 'This episode changed while you were writing, maybe in another tab. Reload it, then apply again.',
  'subject-unavailable': "Basil can't check this episode right now, so it stopped here. Reload to see what went through.",
  'verifier-lost': "Basil's double-check stopped responding, so it stopped here to be safe. Reload the episode to see what went through.",
  'canon-unavailable': "Basil couldn't confirm the episode after the change, so it stopped here to be safe. Reload to see what went through.",
});

/** Where the read has got to (spec §4.3). */
export type BasilPhase = 'idle' | 'reading' | 'ready' | 'read-error';

/** The state word beside the change count, and the class the status bar paints it with. */
export type BasilStatusState = 'working' | 'ok' | 'findings' | 'error';

/**
 * The plan-panel status vocabulary (spec §4.11). Duplicated from the write lane's `RequestStatus`
 * deliberately: the surface must render a plan before the write lane's module exists.
 */
export type BasilRequestStatus =
  | 'planned'
  | 'refused'
  | 'needs-canary'
  | 'needs-owner'
  | 'stale'
  /** T17-08: the run halted before this request's body was built. Proven unsent, and temporary. */
  | 'stopped'
  | 'queued'
  | 'sending'
  | 'reconciled'
  | 'mismatched'
  | 'verified'
  | 'rejected';

/** One row of the plan panel: one request or one structural item, in the writer's words and the engine's. */
export type BasilPlanRow = {
  /** Stable React key: the plan id where there is one, else the request's own coordinates. */
  key: string;
  /** The writer's words for the change, with the scene the script spells. */
  phrase: string;
  /** The engine's words: op, node id, step id, answer id. Advanced-only. */
  engineWords: string;
  /** Where the request stands (spec §4.11). */
  status: BasilRequestStatus;
  /** A refusal message, a warning or a 422 detail, shown verbatim. */
  message?: string;
  /**
   * P5: this row's sentence ends in "Open this scene", so the row offers that exit.
   *
   * It is the PROTECTED exit: the surface asks before discarding unapplied edits, and confirmed
   * changes stay. The row itself never leaves.
   */
  opensScene?: boolean;
  /**
   * P4: the words this change would put in the episode, before and after.
   *
   * Present only for the two ops that change a sentence; null for everything else, which keeps the
   * phrase it already had. The panel leads with these and puts the counts in Details.
   */
  words?: ChangeWords | null;
  /** The chunk this request belongs to; the panel bands rows by it. */
  chunkIndex: number;
  /** The plan id, where a plan was accepted. */
  planId: string | null;
  /** The node the request touches; the panel puts an engine warning under the row it names. */
  branchId: string | null;
  /** The plan's own expected row counts, where it has them. */
  expectedCounts: RowCounts | null;
  /** `edit-context` or `whole-branch`; null for a structural classification. */
  countsMode: 'edit-context' | 'whole-branch' | null;
};

/**
 * The Apply decision, counted over EVERY change row rather than over the plans that survived.
 *
 * `runAnalysis` used to hand the write lane `plans.filter(…)` — the plans the planner accepted —
 * and the surface claimed the full change count beside it. A mixed script would then have applied
 * its sendable subset while the top bar said a larger number. Apply is enabled only when `blocked`
 * is zero; when it is not, the surface says how many rows can be sent, how many cannot, and what
 * the first blocking row itself said.
 */
export type BasilSendability = {
  /** every change row: one per compiled request, one per structural item. */
  total: number;
  /** rows carrying a plan the write lane may send. */
  sendable: number;
  /** rows that cannot be sent: refused, canary-gated, or the owner's to decide. */
  blocked: number;
  /** the first blocking row's own message, verbatim; `null` when nothing blocks. */
  firstBlockingMessage: string | null;
};

/** Nothing analysed yet, or nothing that may be sent: the fail-closed default. */
export const EMPTY_SENDABILITY: BasilSendability = Object.freeze({
  total: 0,
  sendable: 0,
  blocked: 0,
  firstBlockingMessage: null,
});

/** The debounced half of the pipeline, in one object (spec §4.3). */
export type BasilAnalysis = {
  /**
   * The exact script text this analysis was computed from.
   *
   * Apply is bound to it: an edit invalidates sendability synchronously, because the surface
   * compares this against the textarea's current value on every render, so a confirmation can never
   * send plans built from a revision the writer has already moved on from.
   */
  analysedText: string;
  compiled: { mode: 'mapped' | 'fresh'; fixture: Fixture | null; nodes: Branch[] } | null;
  report: StructureReport | null;
  findings: IdentifiedFinding[];
  projection: Projection | null;
  /**
   * The same projection taken over the ORIGINAL read fixture: the preview's `before`.
   *
   * It is computed with the very function that produces `projection`, over the immutable fixture the
   * read lane returned. The verifier's canon of that read is a different serialisation — the spec
   * mandates two independent implementations — so comparing one against the other would mark every
   * scene changed. Like is compared with like here, and the verifier's canon stays the staleness
   * baseline it was built to be.
   */
  beforeProjection: Projection | null;
  plans: Plan[];
  rows: BasilPlanRow[];
  requests: EditRequest[];
  structural: StructuralItem[];
  markedLines: ReadonlySet<number>;
  changeCount: number;
  sendability: BasilSendability;
};

/** An open autocomplete session: what was offered, from where, and which item is active. */
export type AutocompleteSession = {
  /** Offset in the script the completion replaces from. */
  anchor: number;
  /** The words under the caret the list was built from. */
  prefix: string;
  /** The offered items, never empty. */
  items: CompletionItem[];
  /** The caret the list was built at. */
  caret: number;
  /** The item the arrow keys are on. */
  active: number;
};

/** The caret's pixel box, measured from the mirror (editor.js:667-685). */
export type CaretPoint = {
  left: number;
  top: number;
  bottom: number;
  box: { top: number; bottom: number; left: number };
};

/** What the document hook hands back (spec §4.3). */
export type BasilDocument = {
  text: string;
  /**
   * The decompiled text, before the writer touched it (T17-04).
   *
   * Every line number in `map` is a line number of THIS string. The surface needs both to say where
   * a block of the map sits in the script as it stands, so the baseline is handed out rather than
   * kept private to the hook.
   */
  baseline: string;
  map: SidecarMap3 | null;
  names: NamesTable | null;
  setText: (next: string) => void;
  reset: () => void;
  isDirty: boolean;
  errorMessage: string | null;
};

export type { SuppliedNames };
